import { useEffect, useState } from "react";

/** "25/10/2026" to "2026-10-25"; "" for blank; null when it is not a real date. */
export function ddmmyyyyToIso(text: string): string | null {
  const t = text.trim();
  if (t === "") return "";
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
  if (!m) return null;
  const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return null;
  return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Today's date in India (IST), as yyyy-mm-dd, whatever the browser's own zone is. */
export const todayIst = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

export const isoToDdmmyyyy = (iso: string | null | undefined) =>
  iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "";

/**
 * A date the way people here write it, dd/mm/yyyy. The value it hands up is the ISO date
 * (yyyy-mm-dd), or "" when blank; a half-typed or impossible date is held back and flagged.
 */
export default function DateTextInput({
  id,
  value,
  onChange,
  className = "",
  placeholder = "dd/mm/yyyy",
  min,
  onValidity,
}: {
  id?: string;
  value: string;
  onChange: (iso: string) => void;
  className?: string;
  placeholder?: string;
  /** The earliest ISO date allowed, if any. */
  min?: string;
  /** Told whether what is typed is a usable date (blank counts as usable). */
  onValidity?: (ok: boolean) => void;
}) {
  const [text, setText] = useState(isoToDdmmyyyy(value));
  useEffect(() => {
    // Follow the value when it is changed from outside (a form reset).
    if (isoToDdmmyyyy(value) !== text && ddmmyyyyToIso(text) !== value) setText(isoToDdmmyyyy(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const iso = ddmmyyyyToIso(text);
  const tooEarly = !!min && !!iso && iso < min;
  const invalid = iso === null || tooEarly;
  return (
    <>
      <input
        id={id}
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        className={className}
        value={text}
        aria-invalid={invalid}
        onChange={(e) => {
          setText(e.target.value);
          const next = ddmmyyyyToIso(e.target.value);
          const ok = next !== null && !(min && next && next < min);
          onValidity?.(ok);
          // An unusable date clears the value: the earlier valid date is never kept and sent.
          onChange(ok && next !== null ? next : "");
        }}
      />
      {invalid ? (
        <span role="alert" className="text-xs text-[#B00020]">
          {iso === null ? "Use dd/mm/yyyy, for example 25/10/2026." : "That date is in the past."}
        </span>
      ) : null}
    </>
  );
}
